/**
 * B6 — engine `hit` event `zone` strings → visual part slots.
 *
 * The engine resolves damage on seven zones: head, front_ct, rear_ct,
 * l_side, r_side, l_leg, r_leg. The chassis composes from six part
 * slots (chassis, legs, left_arm, right_arm, head, mount). Map zones
 * to the parts that should visually respond on a hit there.
 *
 * Returns a list — some zones touch multiple parts. `null` falls back
 * to "all parts" (whole-body flash, the pre-B6 behavior).
 */

import type { MechState } from "@pilot-protocol/replay-parser";
import type { PartSlot } from "./types";

const ALL_PARTS: ReadonlyArray<PartSlot> = [
  "chassis",
  "legs",
  "left_arm",
  "right_arm",
  "head",
  "mount",
];

/** Engine zone keys on `MechState` that carry an `exposed` flag. */
const ZONE_KEYS = ["head", "front_ct", "rear_ct", "l_side", "r_side", "l_leg", "r_leg"] as const;
type ZoneKey = (typeof ZONE_KEYS)[number];

/**
 * @param zone Lowercase engine zone string from a `hit` event.
 * @returns The parts to flash. `null` means "flash all parts"
 *   (unknown zone — preserve legacy behavior).
 */
export function zoneToParts(zone?: string | null): ReadonlyArray<PartSlot> | null {
  if (!zone) return null;
  switch (zone) {
    case "head":
      return ["head"];
    case "front_ct":
    case "rear_ct":
      return ["chassis"];
    case "l_side":
      return ["left_arm"];
    case "r_side":
      return ["right_arm"];
    case "l_leg":
    case "r_leg":
      return ["legs"];
    default:
      return null;
  }
}

/**
 * B14 — collapse every exposed zone on a `MechState` to the set of
 * visual parts that should render the breach state.
 *
 * "Exposed" is engine state, not an event delta: once a zone's armor
 * hits 0 the `ZoneState.exposed` flag stays true for the rest of the
 * match. Reading from `MechState` rather than the event stream means
 * scrubbing the timeline produces the correct per-tick state for free.
 */
export function exposedPartsOf(mech: MechState): ReadonlySet<PartSlot> {
  const out = new Set<PartSlot>();
  for (const zone of ZONE_KEYS) {
    const z = mech[zone as ZoneKey];
    if (!z || !z.exposed) continue;
    const parts = zoneToParts(zone);
    if (parts) for (const p of parts) out.add(p);
  }
  return out;
}

export { ALL_PARTS };
